import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, X, TrendingUp } from "lucide-react";

/**
 * WelcomeBonusBanner — pivot from the retired Instagram promo (Feb 2026).
 * Highlights the 20% first-deposit bonus on packs of $100 or more, with a
 * one-tap CTA into the Gold Coin Shop. Dismissible per-session so returning
 * players don't get nagged, non-dismissed by default so first-time visitors
 * see it every session until they either dismiss or deposit.
 */
export default function WelcomeBonusBanner() {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem("jr_welcome_bonus_dismissed") === "1"; } catch { return false; }
  });

  if (dismissed) return null;

  const dismiss = (e) => {
    e.stopPropagation();
    try { sessionStorage.setItem("jr_welcome_bonus_dismissed", "1"); } catch {}
    setDismissed(true);
  };

  return (
    <div
      onClick={() => navigate("/store")}
      data-testid="welcome-bonus-banner"
      className="relative overflow-hidden rounded-2xl border cursor-pointer group active:scale-[0.99] transition-transform"
      style={{
        background:
          "radial-gradient(120% 140% at 0% 0%, rgba(77,124,255,0.28) 0%, transparent 55%)," +
          "radial-gradient(120% 140% at 100% 100%, rgba(255,183,77,0.22) 0%, transparent 55%)," +
          "linear-gradient(135deg, #101833 0%, #0b1128 60%, #0a0f24 100%)",
        borderColor: "rgba(255,204,102,0.35)",
        boxShadow: "0 0 32px rgba(77,124,255,0.15) inset, 0 8px 30px rgba(0,0,0,0.35)",
      }}
    >
      {/* Diagonal shimmer accent */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -inset-y-6 -left-10 w-1/3 opacity-30 group-hover:opacity-50 transition-opacity"
        style={{
          background: "linear-gradient(120deg, transparent 0%, rgba(255,215,120,0.35) 45%, transparent 90%)",
          filter: "blur(24px)",
        }}
      />

      <button
        onClick={dismiss}
        className="absolute top-2 right-2 z-10 p-1.5 rounded-full text-amber-200/70 hover:text-white hover:bg-white/10 transition-colors"
        aria-label="Dismiss welcome bonus banner"
        data-testid="welcome-bonus-dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>

      <div className="relative flex items-center gap-4 p-4 sm:p-5">
        <div
          className="hidden sm:flex w-14 h-14 rounded-2xl items-center justify-center shrink-0"
          style={{
            background: "linear-gradient(135deg, #ffcf5f, #b3752a)",
            boxShadow: "0 0 22px rgba(255,204,102,0.45)",
          }}
        >
          <TrendingUp className="w-7 h-7 text-black" strokeWidth={2.5} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <Sparkles className="w-3.5 h-3.5" style={{ color: "#ffcf5f" }} />
            <span
              className="text-[10px] sm:text-[11px] font-black uppercase tracking-wider"
              style={{ color: "#ffcf5f" }}
            >
              Welcome Bonus
            </span>
          </div>
          <h3 className="text-white font-black text-base sm:text-lg leading-tight">
            +20% Bonus on Your First $100+ Deposit
          </h3>
          <p className="text-[11px] sm:text-xs mt-0.5 leading-snug" style={{ color: "#c5cfed" }}>
            Load $100 or more in Gold Coins on your first purchase and we'll drop
            an extra 20% straight into your wallet. Tap to unlock.
          </p>
        </div>

        <div
          className="hidden sm:flex flex-col items-center px-3 py-2 rounded-xl border shrink-0"
          style={{ background: "rgba(255,204,102,0.08)", borderColor: "rgba(255,204,102,0.4)" }}
        >
          <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: "#ffcf5f" }}>
            First Deposit
          </span>
          <span className="text-2xl font-black" style={{ color: "#ffcf5f" }}>+20%</span>
        </div>
      </div>
    </div>
  );
}
